/**
 * Turns a PlanStep (planPhases.ts) into a concise checklist title plus an
 * optional value line, e.g. "Add trigger: Status Progress" with "All" below.
 * Reads the graph *after* the step's ops (so configure_node's data is
 * visible to getSubtitle, not just the raw op) and never throws -- a plan
 * that trips up label generation should still animate onto the canvas with
 * a generic fallback label, not break the reveal.
 */

import {
  getLBNodeInfo,
  getLBNodeType,
  getNodeDisplayName,
} from '../nodes/registry';
import { NodeCategory, type ReactFlowEdge, type ReactFlowNode } from '../types';
import type { OpStepResult } from './applyGraphOps';
import type { GraphOp } from './graphOps';
import type { PlanStep } from './planPhases';

const SUBTITLE_MAX_LENGTH = 72;

export interface StepDescription {
  title: string;
  value?: string;
}

const FALLBACK_DESCRIPTION: StepDescription = { title: 'Update workflow' };

function truncate(text: string): string {
  return text.length > SUBTITLE_MAX_LENGTH
    ? `${text.slice(0, SUBTITLE_MAX_LENGTH - 1)}…`
    : text;
}

/** Walks up parent edges from nodeId looking for the nearest ancestor
 * `conditional` node, returning its display name (the branch label) for
 * the "X → Y" branch-context prefix. Returns undefined if nodeId isn't
 * inside any branch, or IS itself the conditional (an add_branch step
 * describing its own new branch shouldn't find itself as an ancestor). */
function findAncestorBranchLabel(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): string | undefined {
  let currentId = edges.find((e) => e.target === nodeId)?.source;
  const visited = new Set<string>();

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const node = nodes.find((n) => n.id === currentId);
    if (!node) return undefined;
    if (getLBNodeType(node.data as never) === 'conditional') {
      const info = getLBNodeInfo(node.data as never);
      return info ? getNodeDisplayName(node.data as never, info) : undefined;
    }
    currentId = edges.find((e) => e.target === currentId)?.source;
  }
  return undefined;
}

function nodeValue(
  node: ReactFlowNode,
  graph: { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] }
): string | undefined {
  const info = getLBNodeInfo(node.data as never);
  if (!info?.getSubtitle) return undefined;

  let subtitle: React.ReactNode;
  try {
    subtitle = info.getSubtitle(node.data as never, graph);
  } catch {
    return undefined;
  }
  // getSubtitle is typed to return React.ReactNode, not string -- every
  // current node type happens to return a plain string/undefined today,
  // but nothing enforces that. A try/catch alone won't stop a future
  // JSX-returning getSubtitle from rendering as "[object Object]" here.
  if (typeof subtitle !== 'string' || subtitle.length === 0) {
    return undefined;
  }
  return truncate(subtitle);
}

function stepTitle(node: ReactFlowNode, primaryOp: GraphOp): string {
  const info = getLBNodeInfo(node.data as never);
  if (!info) return 'Add node';
  const nodeType = getLBNodeType(node.data as never);
  const displayName = getNodeDisplayName(node.data as never, info);

  if (primaryOp.op === 'add_branch') {
    return `Add path: ${displayName}`;
  }
  if (nodeType === 'ai_call_action') {
    const isHandoff =
      node.data.ai_call_action?.is_transfer_escalation === true ||
      /handoff|escalat/i.test(displayName);
    return isHandoff ? 'Add AI call: Handoff' : 'Add AI agent';
  }
  if (info.category === NodeCategory.TRIGGER) {
    return `Add trigger: ${info.label.replace(/\s+trigger$/i, '')}`;
  }
  if (info.category === NodeCategory.NOTIFICATIONS) {
    return `Add ${info.label} notification`;
  }
  if (nodeType === 'paths') return 'Add paths';
  return `Add ${info.label}`;
}

function existingNodeTitle(node: ReactFlowNode): string {
  const info = getLBNodeInfo(node.data as never);
  if (!info) return 'Add node';
  const nodeType = getLBNodeType(node.data as never);
  const displayName = getNodeDisplayName(node.data as never, info);
  if (nodeType === 'conditional') {
    return `Add path: ${displayName}`;
  }
  if (nodeType === 'ai_call_action') {
    const isHandoff =
      node.data.ai_call_action?.is_transfer_escalation === true ||
      /handoff|escalat/i.test(displayName);
    return isHandoff ? 'Add AI call: Handoff' : 'Add AI agent';
  }
  if (info.category === NodeCategory.TRIGGER) {
    return `Add trigger: ${info.label.replace(/\s+trigger$/i, '')}`;
  }
  if (info.category === NodeCategory.NOTIFICATIONS) {
    return `Add ${info.label} notification`;
  }
  if (nodeType === 'paths') return 'Add paths';
  return `Add ${info.label}`;
}

/** Describe a node from a persisted workflow when the original GraphOps and
 * in-memory reveal checkpoints are intentionally no longer available. */
export function describeExistingNode(
  node: ReactFlowNode,
  graph: { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] }
): StepDescription {
  try {
    const branchLabel = findAncestorBranchLabel(
      node.id,
      graph.nodes,
      graph.edges
    );
    const value = [branchLabel, nodeValue(node, graph)]
      .filter((part): part is string => Boolean(part))
      .join(' — ');
    return {
      title: existingNodeTitle(node),
      value: value || undefined,
    };
  } catch {
    return FALLBACK_DESCRIPTION;
  }
}

export function describeStep(
  step: PlanStep,
  ops: GraphOp[],
  opSteps: OpStepResult[]
): StepDescription {
  try {
    const primaryOp = ops[step.primaryOpIndex];

    if (primaryOp.op === 'delete_node') {
      return { title: 'Remove a step' };
    }

    const lastOpIndex = step.opIndices[step.opIndices.length - 1];
    const graphState = opSteps[lastOpIndex];
    if (!step.nodeId || !graphState) {
      return FALLBACK_DESCRIPTION;
    }

    const node = graphState.nodes.find((n) => n.id === step.nodeId);
    if (!node) return FALLBACK_DESCRIPTION;

    const graph = { nodes: graphState.nodes, edges: graphState.edges };
    const branchLabel = findAncestorBranchLabel(
      step.nodeId,
      graphState.nodes,
      graphState.edges
    );
    const value = [branchLabel, nodeValue(node, graph)]
      .filter((part): part is string => Boolean(part))
      .join(' — ');
    return {
      title: stepTitle(node, primaryOp),
      value: value || undefined,
    };
  } catch {
    return FALLBACK_DESCRIPTION;
  }
}
